/**
 * Servizio Ticket di Supporto - lato Associato
 * Chiamate REST verso /api/support/* con token OTP associato.
 */

import axios from 'axios';
import { getAuthHeader } from './otpAuthService';
import type { CampiExtra, CampoRichiesta } from './supportApi';

// REST: usare REACT_APP_API_BASE_URL (include già /api), NON REACT_APP_API_URL (GraphQL).
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

// --- Tipi ---

export interface SupportGroupLite {
  _id: string;
  nome: string;
  descrizione: string;
  slug?: string;
  /** Il backend esclude gia i canali nascosti agli associati (Task 31): difesa in profondita. */
  hiddenFromAssociates?: boolean;
  /** Avatar del canale: nella select si mostra solo l'emoji (le option non reggono immagini). */
  avatar?: { kind: 'emoji' | 'image'; emoji?: string | null; url?: string | null } | null;
  /** Campi facoltativi del canale («Documento Unico»); assente sui server di prima = nessuno. */
  campiRichiesta?: CampoRichiesta[];
}

export interface ThreadMessage {
  authorType: 'associate' | 'representative';
  authorName?: string;
  body: string;
  sentAt: string;
  attachments?: { url: string; filename: string }[];
}

export interface SupportTicketListItem {
  _id: string;
  ticketNumber: string;
  subject: string;
  status: string;
  priority: string;
  groupId?: { nome: string };
  lastActivityAt: string;
  /** Apertura della pratica (la lista richiedente mostra "aperta il"). */
  createdAt?: string;
  unread?: { associate?: boolean };
}

export interface SupportTicketDetail {
  _id: string;
  ticketNumber: string;
  subject: string;
  status: string;
  priority: string;
  canale?: string;
  publicThread: ThreadMessage[];
  rating?: { stars: number; comment?: string };
  resolvedAt?: string;
  closedAt?: string;
  /** «Dati della pratica»: valori salvati (assente se vuoto). */
  campiExtra?: CampiExtra;
  /** Etichette attuali dei campi del canale del ticket (`[]` se il canale non ne ha). */
  campiRichiesta?: CampoRichiesta[];
}

export interface PaginatedTickets {
  items: SupportTicketListItem[];
  total: number;
  page: number;
  totalPages: number;
}

// --- Funzioni ---

/**
 * Elenco gruppi/canali di supporto disponibili
 */
export const getGroups = async (): Promise<SupportGroupLite[]> => {
  const response = await axios.get(`${API_BASE_URL}/support/groups`, {
    headers: getAuthHeader() as any,
  });
  return response.data.data;
};

/**
 * Elenco paginato dei ticket dell'associato
 */
export const getMyTickets = async (
  params: { page?: number; status?: string; limit?: number }
): Promise<PaginatedTickets> => {
  const query = new URLSearchParams();
  if (params.page) query.append('page', params.page.toString());
  if (params.status) query.append('status', params.status);
  if (params.limit) query.append('limit', params.limit.toString());

  const response = await axios.get(
    `${API_BASE_URL}/support/tickets?${query.toString()}`,
    { headers: getAuthHeader() as any }
  );
  return response.data.data;
};

/**
 * Dettaglio singolo ticket
 */
export const getTicket = async (id: string): Promise<SupportTicketDetail> => {
  const response = await axios.get(`${API_BASE_URL}/support/tickets/${id}`, {
    headers: getAuthHeader() as any,
  });
  return response.data.data;
};

/**
 * Crea un nuovo ticket
 */
export const createTicket = async (
  p: {
    groupId?: string; subject: string; priority: string; body: string; files?: File[]; tags?: string[];
    /** Campi facoltativi del canale: viaggiano come stringa JSON nel multipart. */
    campiExtra?: CampiExtra;
  }
): Promise<{ _id: string; ticketNumber: string }> => {
  // groupId assente = canale predefinito di smistamento (deciso dal backend).
  let payload: any = { subject: p.subject, priority: p.priority, body: p.body };
  if (p.groupId) payload.groupId = p.groupId;
  if (p.tags && p.tags.length > 0) payload.tags = p.tags;
  const conCampi = Boolean(p.campiExtra && Object.keys(p.campiExtra).length > 0);
  // Senza file e senza campi la richiesta resta JSON, identica a prima.
  if ((p.files && p.files.length > 0) || conCampi) {
    const fd = new FormData();
    if (p.groupId) fd.append('groupId', p.groupId);
    fd.append('subject', p.subject);
    fd.append('priority', p.priority);
    fd.append('body', p.body);
    // Con FormData i tag viaggiano come stringa CSV (il backend accetta entrambe le forme).
    if (p.tags && p.tags.length > 0) fd.append('tags', p.tags.join(','));
    if (conCampi) fd.append('campiExtra', JSON.stringify(p.campiExtra));
    (p.files || []).forEach((f) => fd.append('files', f));
    payload = fd;
  }
  // Con FormData non impostiamo Content-Type: axios aggiunge il boundary corretto.
  const response = await axios.post(`${API_BASE_URL}/support/tickets`, payload, {
    headers: getAuthHeader() as any,
  });
  return response.data.data;
};

/**
 * Invia un messaggio sul thread pubblico del ticket
 */
export const sendMessage = async (
  id: string,
  body: string,
  files?: File[]
): Promise<void> => {
  let payload: any = { body };
  if (files && files.length > 0) {
    const fd = new FormData();
    fd.append('body', body);
    files.forEach((f) => fd.append('files', f));
    payload = fd;
  }
  await axios.post(
    `${API_BASE_URL}/support/tickets/${id}/messages`,
    payload,
    { headers: getAuthHeader() as any }
  );
};

/**
 * Valuta un ticket risolto/chiuso
 */
export const rateTicket = async (
  id: string,
  stars: number,
  comment?: string
): Promise<void> => {
  await axios.post(
    `${API_BASE_URL}/support/tickets/${id}/rating`,
    { stars, comment },
    { headers: getAuthHeader() as any }
  );
};

/**
 * Riapre un ticket chiuso (entro la finestra consentita)
 */
export const reopenTicket = async (id: string): Promise<void> => {
  await axios.post(
    `${API_BASE_URL}/support/tickets/${id}/reopen`,
    {},
    { headers: getAuthHeader() as any }
  );
};

/**
 * Scarica un allegato del ticket tramite endpoint autenticato e lo apre in una
 * nuova scheda. `storedName` e il nome file salvato (att.url), `displayName` il
 * nome originale (att.filename), tenuto per coerenza/uso futuro.
 */
export const openAttachment = async (
  ticketId: string,
  storedName: string,
  displayName?: string
): Promise<void> => {
  // Apri la tab SINCRONAMENTE (dentro il gesto utente) per non farla bloccare dal
  // popup blocker; poi naviga al blob una volta scaricato.
  const win = window.open('', '_blank');
  try {
    const resp = await axios.get(
      `${API_BASE_URL}/support/tickets/${ticketId}/attachments/${encodeURIComponent(storedName)}`,
      { headers: getAuthHeader() as any, responseType: 'blob' }
    );
    const url = URL.createObjectURL(resp.data as Blob);
    if (win) {
      win.location.href = url;
    } else {
      // Popup bloccato: fallback a download via anchor
      const a = document.createElement('a');
      a.href = url;
      a.download = displayName || storedName;
      a.click();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    if (win) win.close();
    throw err;
  }
};
